import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { type DragEndEvent } from '@dnd-kit/core';
import { apiService } from './ApiService';
import { syncService, actualizeWhileOnline } from './SyncService';
import { type CategoryInfo } from '../models/CategoryInfo';

/**
 * Shared category rail state, mounted once in AppShell across Dashboard/Notes.
 * Categories load once (ordered + counted). Mode-aware: dashboard navigates;
 * notes mode defers to the Notes page via setNotes()/clearNotes().
 */

export type RailMode = 'dashboard' | 'notes';

/** Dynamic, Notes-page-owned state the rail needs while in notes mode. */
export interface NotesRailState {
  activeCategoryId: string;
  // Lets "All notes" return to the same note instead of the server's activeNoteId
  lastNoteId?: string;
  isArchive: boolean;
  isSearchMode: boolean;
  searchQuery: string;
  searchCount: number;
  // Derived from the loaded note list so they stay correct under the Archive filter
  noteCounts: Record<string, number>;
  totalNoteCount: number;
}

/** Notes-page-owned callbacks (stable bound methods). */
export interface NotesRailHandlers {
  onAllClick: () => void;
  onCategoryClick: (cat: CategoryInfo) => void;
  onAddCategory: () => void;
  onCategoryDragEnd?: (event: DragEndEvent) => void;
  onCategoryEdit?: (cat: CategoryInfo) => void;
}

export class CategoryRailStore {
  @observable.ref categories: CategoryInfo[] = [];
  @observable isLoading = true;
  // Once first load succeeds, load() becomes a one-shot (no skeleton flash)
  @observable isLoaded = false;
  @observable mode: RailMode = 'dashboard';
  // Mobile rail drawer (dashboard mode only; notes mode uses its own drawer)
  @observable isMobileRailOpen = false;
  @observable isRailHidden = false; // desktop collapse, persisted across modes

  @observable.ref notesState: NotesRailState | null = null;
  private _notesHandlers: NotesRailHandlers | null = null;
  // Persists past notes-bridge clear; lets "All notes" return to the last-open note
  lastNoteId: string | null = null;
  // Set briefly when "All notes" is clicked from Dashboard; consumed once by Notes on load
  pendingAllNotes: boolean = false;

  private _loading: Promise<void> | null = null;

  constructor() {
    makeObservable(this);
  }

  /** Load categories once (idempotent). */
  load(): Promise<void> {
    if (this._loading) return this._loading;
    if (this.isLoaded) return Promise.resolve();
    this._loading = (async () => {
      const firstLoad = this.categories.length === 0;
      // Skeleton only on the first load; a refresh (post-CRUD) keeps the list visible.
      if (firstLoad) runInAction(() => { this.isLoading = true; });
      try {
        const cats = await apiService.get<CategoryInfo[]>('/api/note/category', null);
        runInAction(() => {
          this.categories = cats;
          this.isLoading = false;
          this.isLoaded = true;
        });
        // A post-CRUD refresh() already fetched fresh data, so actualize only on first load.
        if (firstLoad) {
          syncService.markCached();
          actualizeWhileOnline(async () => {
            const fresh = await apiService.get<CategoryInfo[]>('/api/note/category', null, { actualize: true });
            runInAction(() => { this.categories = fresh; });
          });
        }
      } catch {
        runInAction(() => { this.isLoading = false; });
      } finally {
        this._loading = null;
      }
    })();
    return this._loading;
  }

  /** Force re-fetch (after category CRUD). */
  @action
  refresh(): Promise<void> {
    this._loading = null;
    this.isLoaded = false;
    return this.load();
  }

  @action
  requestAllNotes(): void {
    this.pendingAllNotes = true;
  }

  @action
  clearPendingAllNotes(): void {
    this.pendingAllNotes = false;
  }

  @action
  setMode(mode: RailMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.isMobileRailOpen = false; // leaving dashboard closes its mobile drawer
  }

  @action
  toggleMobileRail(): void {
    this.isMobileRailOpen = !this.isMobileRailOpen;
  }

  @action
  setMobileRailOpen(open: boolean): void {
    this.isMobileRailOpen = open;
  }

  @action
  toggleRail(): void {
    this.isRailHidden = !this.isRailHidden;
  }

  /** Counts + total from loaded categories (dashboard mode). "Default" is keyed by '' to match CategoryRail/Notes. */
  @computed
  get dashboardNoteCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const c of this.categories) {
      counts[c.id === 'default' ? '' : c.id] = c.count ?? 0;
    }
    return counts;
  }

  @computed
  get dashboardTotalNoteCount(): number {
    return this.categories.reduce((sum, c) => sum + (c.count ?? 0), 0);
  }

  @action
  setNotesHandlers(handlers: NotesRailHandlers): void {
    this._notesHandlers = handlers;
  }

  /** Pushed from a MobX reaction so the rail stays in sync with Notes observables. */
  @action
  setNotesState(state: NotesRailState): void {
    this.notesState = state;
    if (state.lastNoteId) this.lastNoteId = state.lastNoteId;
  }

  @action
  clearNotes(): void {
    this.notesState = null;
    this._notesHandlers = null;
  }

  get notesHandlers(): NotesRailHandlers | null {
    return this._notesHandlers;
  }
}

export const categoryRailStore = new CategoryRailStore();
